// ═══════════════════════════════════════════════════════════════
// Map Engine — Camera (pan / zoom / coordinate transforms)
// ═══════════════════════════════════════════════════════════════

const MIN_SCALE = 0.4;
const MAX_SCALE = 2;
const ZOOM_IN = 1.07;
const ZOOM_OUT = 0.93;

export class Camera {
  scale: number;
  offsetX: number;
  offsetY: number;

  private defaults: { scale: number; offsetX: number; offsetY: number };

  constructor(scale = 0.85, offsetX = 50, offsetY = 30) {
    this.scale = scale;
    this.offsetX = offsetX;
    this.offsetY = offsetY;
    this.defaults = { offsetX, offsetY, scale };
  }

  /** Convert screen pixel coordinates → world coordinates */
  screenToWorld(sx: number, sy: number) {
    return {
      x: (sx - this.offsetX) / this.scale,
      y: (sy - this.offsetY) / this.scale,
    };
  }

  /** Convert world coordinates → screen pixel coordinates */
  worldToScreen(wx: number, wy: number) {
    return {
      x: wx * this.scale + this.offsetX,
      y: wy * this.scale + this.offsetY,
    };
  }

  /** Zoom centered on a screen point. deltaY > 0 = zoom out */
  zoom(screenX: number, screenY: number, deltaY: number) {
    const factor = deltaY > 0 ? ZOOM_OUT : ZOOM_IN;
    const next = Math.min(Math.max(this.scale * factor, MIN_SCALE), MAX_SCALE);
    const ratio = next / this.scale;
    this.offsetX = screenX - ratio * (screenX - this.offsetX);
    this.offsetY = screenY - ratio * (screenY - this.offsetY);
    this.scale = next;
  }

  /** Pinch-zoom: apply a scale ratio centered on a screen point */
  pinchZoom(screenX: number, screenY: number, ratio: number) {
    const next = Math.min(Math.max(this.scale * ratio, MIN_SCALE), MAX_SCALE);
    const r = next / this.scale;
    this.offsetX = screenX - r * (screenX - this.offsetX);
    this.offsetY = screenY - r * (screenY - this.offsetY);
    this.scale = next;
  }

  /** Reset to initial values */
  reset() {
    this.scale = this.defaults.scale;
    this.offsetX = this.defaults.offsetX;
    this.offsetY = this.defaults.offsetY;
  }

  /** Fit the camera so the given world-space bounding box fills the
   *  viewport with padding. ``topInset`` is a floor on how close the
   *  content's top edge can sit to the canvas top — useful for a
   *  floating brand mark. When the natural centered position would
   *  push the content above ``topInset`` the camera is nudged down;
   *  otherwise the centering is left alone.
   */
  fitToBounds(
    bounds: { minX: number; minY: number; maxX: number; maxY: number },
    viewportW: number,
    viewportH: number,
    padding = 60,
    topInset = 0,
  ) {
    const contentW = bounds.maxX - bounds.minX;
    const contentH = bounds.maxY - bounds.minY;
    if (contentW <= 0 || contentH <= 0 || viewportW <= 0 || viewportH <= 0) {
      return;
    }

    // Two-stage scale: first try to fit using the full vertical area
    // (so the floor doesn't shrink just because we reserved space for
    // a brand mark). If that would push the content below the bottom
    // padding once we shift it past the top inset, re-scale to fit
    // within the reduced band [topInset, viewportH - padding].
    const availW = viewportW - padding * 2;
    const fullAvailH = viewportH - padding * 2;
    const insetAvailH = fullAvailH - topInset;
    let scale = fullAvailH / contentH;
    if (contentW * scale > availW) {
      scale = availW / contentW;
    }
    if (topInset > 0 && contentH * scale > insetAvailH) {
      // Centered content would overflow once shifted past the inset —
      // re-scale to fit the reduced band.
      scale = insetAvailH / contentH;
      if (contentW * scale > availW) {
        scale = availW / contentW;
      }
    }
    this.scale = Math.max(MIN_SCALE, Math.min(scale, MAX_SCALE));

    const scaledW = contentW * this.scale;
    const scaledH = contentH * this.scale;
    this.offsetX = (viewportW - scaledW) / 2 - bounds.minX * this.scale;

    // Vertical centering — but if the result would put the content's
    // top edge above ``topInset``, push it down to clear the inset.
    const centeredOffsetY = (viewportH - scaledH) / 2 - bounds.minY * this.scale;
    const contentTopY = centeredOffsetY + bounds.minY * this.scale;
    if (topInset > 0 && contentTopY < topInset) {
      this.offsetY = topInset - bounds.minY * this.scale;
    } else {
      this.offsetY = centeredOffsetY;
    }
  }

  get zoomPercent(): number {
    return Math.round(this.scale * 100);
  }
}
